import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { ComparisonTable } from "@/components/content/ComparisonTable";
import { VerdictBox } from "@/components/content/VerdictBox";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { LeadFormSection } from "@/components/LeadFormSection";
import { NavLink } from "@/components/NavLink";

const OneTimeVsSubscription = () => {
  const comparisonData = [
    { feature: "Upfront Cost", optionA: "$3,000-$15,000", optionB: "$0-$500" },
    { feature: "Monthly Cost", optionA: "$0 (hosting separate)", optionB: "$150-$500" },
    { feature: "3-Year Total", optionA: "$4,000-$20,000", optionB: "$5,400-$18,000" },
    { feature: "Ongoing Updates", optionA: "Extra cost or DIY", optionB: "Included" },
    { feature: "SEO Optimization", optionA: "One-time setup", optionB: "Continuous" },
    { feature: "Design Refreshes", optionA: "Major fee", optionB: "Often included" },
    { feature: "Support Response", optionA: "Pay per incident", optionB: "Priority included" },
    { feature: "Technology Updates", optionA: "You manage or pay", optionB: "Handled for you" },
  ];

  const verdicts = [
    { label: "Best for Growth", recommendation: "Subscription" },
    { label: "Best for Budget", recommendation: "Depends on timeline" },
    { label: "Most Hands-Off", recommendation: "Subscription" },
    { label: "Maximum Ownership", recommendation: "One-time" },
    { label: "Best Results", recommendation: "Subscription with optimization" },
  ];

  const faqs = [
    {
      question: "Is a website subscription worth it for lawn care businesses?",
      answer: "For most lawn care businesses, yes. Subscriptions include ongoing SEO, updates, and support that one-time builds don't. The continuous optimization typically generates more leads over time."
    },
    {
      question: "How much does a one-time lawn care website cost?",
      answer: "Professional one-time builds range from $3,000-$15,000+ depending on complexity. Add $100-$500/month for hosting, maintenance, and eventual updates to get the true cost."
    },
    {
      question: "What's included in a website subscription?",
      answer: "Quality subscriptions include: professional design, SEO optimization, content updates, security maintenance, hosting, support, and ongoing improvements. Ours also includes lawn care industry expertise."
    },
    {
      question: "Can I cancel a website subscription anytime?",
      answer: "Policies vary. Some services have minimum terms (usually 3-12 months). Check cancellation terms and what happens to your site if you leave. Some let you keep it; others don't."
    },
    {
      question: "Which is cheaper long-term?",
      answer: "Over 3-5 years, costs are often similar. But subscriptions typically generate more leads due to ongoing optimization, making the ROI better even if monthly costs add up."
    },
    {
      question: "Do I own my website with a subscription?",
      answer: "It varies by provider. Some subscriptions let you buy out and own the site. Others retain ownership. Ask before signing—ownership terms matter for your exit strategy."
    },
    {
      question: "What happens to my site if I stop paying?",
      answer: "With one-time sites, you own it forever (just pay hosting). With subscriptions, the site may go offline unless you own the final product. Clarify this before committing."
    },
    {
      question: "Which option gets more leads?",
      answer: "Subscriptions with ongoing SEO typically generate 30-50% more leads than static one-time sites. The continuous optimization keeps you competitive as algorithms and competitors change."
    },
    {
      question: "Should I choose one-time or subscription for a new lawn care business?",
      answer: "Subscription is often better for new businesses. Lower upfront cost, professional results from day one, and ongoing optimization help you compete while building your reputation."
    },
    {
      question: "Can I upgrade from one-time to subscription later?",
      answer: "Usually yes, but you may need a new build. If starting with a one-time site, ensure it's built on a platform (like WordPress) that supports easy transition to managed services."
    },
  ];

  const CANONICAL = "https://www.leafrushmarketing.com/comparisons/one-time-vs-subscription-website/";

  return (
    <>
      <SEO 
        title="One-Time vs Subscription Lawn Care Websites"
        description="One-time vs subscription websites for lawn care: Compare upfront costs, ongoing value, and lead generation. Find the best model for your business."
        canonical={CANONICAL}
        ogUrl={CANONICAL}
      />
      <PageSchema 
        title="One-Time vs Subscription Lawn Care Websites"
        description="One-time vs subscription websites for lawn care: Compare upfront costs, ongoing value, and lead generation."
        url={CANONICAL}
        faqs={faqs}
      />
      
      <article className="py-16">
        <div className="container px-4 md:px-6">
          <LastUpdated />
          
          <header className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              One-Time vs Subscription: Which Website Model Wins?
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Pay once and own it, or subscribe for ongoing results? Here's the honest comparison for lawn care businesses.
            </p>
          </header>

          <TopCTA />

          <div className="max-w-4xl mx-auto">
            <VerdictBox verdicts={verdicts} />

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Cost & Value Comparison</h2>
              <p className="text-muted-foreground mb-6">
                When investing in <NavLink to="/lawn-care-website-design/" className="text-primary hover:underline">lawn care website design</NavLink>, 
                the payment model affects both your budget and long-term results.
              </p>
              <ComparisonTable 
                optionAName="One-Time Build"
                optionBName="Subscription"
                rows={comparisonData}
              />
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">The Hidden Costs of One-Time Websites</h2>
              <p className="text-muted-foreground mb-4">
                That $5,000 one-time website isn't really one-time. Here's what you'll pay over 3 years:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <span className="text-destructive font-bold">+</span>
                  <span>Hosting: $300-$600/year ($900-$1,800 total)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-destructive font-bold">+</span>
                  <span>Security/maintenance: $500-$2,000/year</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-destructive font-bold">+</span>
                  <span>Design updates: $500-$2,000 per refresh</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-destructive font-bold">+</span>
                  <span>SEO updates: $200-$500 per optimization</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-destructive font-bold">=</span>
                  <span><strong>True 3-year cost: $8,000-$20,000+</strong></span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Why Subscriptions Win for Lead Generation</h2>
              <p className="text-muted-foreground mb-4">
                Static websites lose ground to competitors who continuously optimize. Subscription websites include:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>Ongoing <NavLink to="/lawn-care-website-seo/" className="text-primary hover:underline">SEO optimization</NavLink> as algorithms change</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>Fresh content and <NavLink to="/service-pages-that-rank/" className="text-primary hover:underline">service page updates</NavLink></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span><NavLink to="/lawn-care-quote-form/" className="text-primary hover:underline">Form optimization</NavLink> for better conversions</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">✓</span>
                  <span>Speed and security updates handled for you</span>
                </li>
              </ul>
            </section>

            <MidPageCTA />

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-6">Our Website Subscription</h2>
              <p className="text-muted-foreground mb-4">
                Our <NavLink to="/websites/" className="text-primary hover:underline">lawn care website subscription</NavLink> includes 
                everything you need—no surprise costs, no technical hassles.
              </p>
              <div className="bg-primary/10 p-6 rounded-lg mb-6">
                <h3 className="font-bold mb-3">What's Included:</h3>
                <ul className="space-y-2">
                  <li>• Professional lawn care website design</li>
                  <li>• <NavLink to="/service-area-pages-local-seo/" className="text-primary hover:underline">Service area pages</NavLink> for local SEO</li>
                  <li>• Ongoing SEO optimization</li>
                  <li>• Hosting, security, and backups</li>
                  <li>• Priority support and updates</li>
                </ul>
              </div>
              <p className="text-muted-foreground">
                Add <NavLink to="/growth-add-ons/gbp/" className="text-primary hover:underline">Google Business Profile</NavLink> and 
                <NavLink to="/growth-add-ons/google-ads/" className="text-primary hover:underline"> Google Ads</NavLink> for 
                accelerated lead generation.
              </p>
            </section>

            <FAQSection faqs={faqs} />

            <InternalLinks 
              links={[
                { href: "/comparisons/agency-vs-freelancer-website-lawn-care/", text: "Agency vs Freelancer" },
                { href: "/comparisons/cheap-vs-professional-lawn-care-website/", text: "Cheap vs Professional" },
                { href: "/lawn-care-website-cost/", text: "Website Cost Guide" },
                { href: "/landscaping-website-cost/", text: "Landscaping Website Costs" },
              ]}
            />
          </div>
        </div>
      </article>

      <BottomCTA />
      
      <LeadFormSection title="Schedule Your Free Consultation" />
    </>
  );
};

export default OneTimeVsSubscription;
